import { useState, useCallback } from "react";
import { functions } from "../config/firebase";
import { httpsCallable } from "firebase/functions";

/**
 * Blog post section structure
 */
export interface BlogSection {
  heading: string;
  content: string;
  subheadings?: { heading: string; content: string }[];
}

/**
 * Full blog post structure
 */
export interface BlogPost {
  title: string;
  metaDescription: string;
  excerpt: string;
  targetKeyword: string;
  secondaryKeywords: string[];
  sections: BlogSection[];
  wordCount: number;
  readingTime: string;
  internalLinkSuggestions: string[];
  conclusion: string;
  callToAction: string;
}

/**
 * Social media post for a specific platform
 */
export interface SocialPost {
  platform: "instagram" | "linkedin" | "twitter" | "tiktok" | "facebook";
  content: string;
  hashtags: string[];
  imagePrompt?: string;
  tone: "casual" | "professional" | "urgent";
  characterCount: number;
}

/**
 * Instagram carousel/thread content
 */
export interface CarouselContent {
  slides: { heading: string; content: string; imagePrompt: string }[];
  caption: string;
  hashtags: string[];
}

/**
 * Social media content collection
 */
export interface SocialMediaContent {
  instagram: SocialPost[];
  linkedin: SocialPost[];
  twitter: SocialPost[];
  tiktok: SocialPost[];
  facebook: SocialPost[];
  carousels: CarouselContent[];
}

/**
 * Individual email in a sequence
 */
export interface Email {
  subject: string;
  subjectVariants: string[];
  previewText: string;
  body: string;
  callToAction: string;
  sendDay: number;
  personalizationTags: string[];
}

/**
 * Email marketing sequence
 */
export interface EmailSequence {
  name: string;
  type: "welcome" | "nurture" | "launch" | "reengagement";
  description: string;
  emails: Email[];
  recommendedTiming: string[];
  totalEmails: number;
}

/**
 * Video script section
 */
export interface ScriptSection {
  timestamp: string;
  type: "hook" | "intro" | "main" | "cta" | "outro";
  content: string;
  visualNotes?: string;
}

/**
 * Full video script
 */
export interface VideoScript {
  platform: "youtube" | "tiktok" | "reels" | "explainer";
  title: string;
  duration: string;
  hook: string;
  hookVariants: string[];
  sections: ScriptSection[];
  cta: string;
  bRollSuggestions: string[];
  thumbnailIdeas: string[];
  description?: string;
  tags?: string[];
}

/**
 * Content repurposing result
 */
export interface RepurposedContent {
  sourceType: string;
  targetType: string;
  content: BlogPost | SocialPost[] | EmailSequence | VideoScript;
}

/**
 * Full content creator result
 */
export interface ContentCreatorResult {
  blogPosts: BlogPost[];
  socialMedia: SocialMediaContent;
  emailSequences: EmailSequence[];
  videoScripts: VideoScript[];
  generatedAt: string;
}

/**
 * Hook for AI-powered content creation
 * Following the same pattern as useSEOAgent and useMarketingPlan
 */
export const useContentCreator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [socialMedia, setSocialMedia] = useState<SocialMediaContent | null>(null);
  const [emailSequences, setEmailSequences] = useState<EmailSequence[]>([]);
  const [videoScripts, setVideoScripts] = useState<VideoScript[]>([]);

  /**
   * Generate a full blog post from a topic or keyword
   */
  const generateBlogPost = useCallback(
    async (
      projectId: string,
      topic: string,
      targetKeyword?: string,
      wordCount: number = 1500
    ): Promise<BlogPost | null> => {
      setIsGenerating(true);
      setError(null);

      try {
        const generateFn = httpsCallable<
          { projectId: string; topic: string; targetKeyword?: string; wordCount: number },
          { blogPost: BlogPost }
        >(functions, "generateBlogPost");

        const result = await generateFn({ projectId, topic, targetKeyword, wordCount });
        const blogPost = result.data.blogPost;

        setBlogPosts((prev) => [...prev, blogPost]);
        return blogPost;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to generate blog post";
        setError(errorMessage);
        console.error("Blog post generation error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  /**
   * Generate social media content for multiple platforms
   */
  const generateSocialContent = useCallback(
    async (
      projectId: string,
      topic: string,
      platforms: ("instagram" | "linkedin" | "twitter" | "tiktok" | "facebook")[] = [
        "instagram",
        "linkedin",
        "twitter",
      ],
      postsPerPlatform: number = 3
    ): Promise<SocialMediaContent | null> => {
      setIsGenerating(true);
      setError(null);

      try {
        const generateFn = httpsCallable<
          { projectId: string; topic: string; platforms: string[]; postsPerPlatform: number },
          { socialContent: SocialMediaContent }
        >(functions, "generateSocialContent");

        const result = await generateFn({ projectId, topic, platforms, postsPerPlatform });
        const socialContent = result.data.socialContent;

        setSocialMedia(socialContent);
        return socialContent;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to generate social content";
        setError(errorMessage);
        console.error("Social content generation error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  /**
   * Generate an email marketing sequence
   */
  const generateEmailSequence = useCallback(
    async (
      projectId: string,
      sequenceType: "welcome" | "nurture" | "launch" | "reengagement",
      emailCount: number = 5
    ): Promise<EmailSequence | null> => {
      setIsGenerating(true);
      setError(null);

      try {
        const generateFn = httpsCallable<
          { projectId: string; sequenceType: string; emailCount: number },
          { emailSequence: EmailSequence }
        >(functions, "generateEmailSequence");

        const result = await generateFn({ projectId, sequenceType, emailCount });
        const emailSequence = result.data.emailSequence;

        setEmailSequences((prev) => [...prev, emailSequence]);
        return emailSequence;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to generate email sequence";
        setError(errorMessage);
        console.error("Email sequence generation error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  /**
   * Generate a video script for a specific platform
   */
  const generateVideoScript = useCallback(
    async (
      projectId: string,
      topic: string,
      platform: "youtube" | "tiktok" | "reels" | "explainer",
      duration: "short" | "medium" | "long" = "medium"
    ): Promise<VideoScript | null> => {
      setIsGenerating(true);
      setError(null);

      try {
        const generateFn = httpsCallable<
          { projectId: string; topic: string; platform: string; duration: string },
          { videoScript: VideoScript }
        >(functions, "generateVideoScript");

        const result = await generateFn({ projectId, topic, platform, duration });
        const videoScript = result.data.videoScript;

        setVideoScripts((prev) => [...prev, videoScript]);
        return videoScript;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to generate video script";
        setError(errorMessage);
        console.error("Video script generation error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  /**
   * Repurpose content from one format to another
   */
  const repurposeContent = useCallback(
    async (
      projectId: string,
      sourceType: "blog" | "social" | "email" | "video",
      sourceContent: string,
      targetType: "blog" | "social" | "email" | "video"
    ): Promise<RepurposedContent | null> => {
      setIsGenerating(true);
      setError(null);

      try {
        const generateFn = httpsCallable<
          { projectId: string; sourceType: string; sourceContent: string; targetType: string },
          { repurposedContent: RepurposedContent }
        >(functions, "repurposeContent");

        const result = await generateFn({ projectId, sourceType, sourceContent, targetType });
        return result.data.repurposedContent;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to repurpose content";
        setError(errorMessage);
        console.error("Content repurposing error:", err);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  /**
   * Clear all generated content
   */
  const clearContent = useCallback(() => {
    setBlogPosts([]);
    setSocialMedia(null);
    setEmailSequences([]);
    setVideoScripts([]);
    setError(null);
  }, []);

  /**
   * Remove a specific blog post
   */
  const removeBlogPost = useCallback((index: number) => {
    setBlogPosts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Remove a specific email sequence
   */
  const removeEmailSequence = useCallback((index: number) => {
    setEmailSequences((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Remove a specific video script
   */
  const removeVideoScript = useCallback((index: number) => {
    setVideoScripts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return {
    // State
    isGenerating,
    error,
    blogPosts,
    socialMedia,
    emailSequences,
    videoScripts,

    // Generation methods
    generateBlogPost,
    generateSocialContent,
    generateEmailSequence,
    generateVideoScript,
    repurposeContent,

    // Management methods
    clearContent,
    removeBlogPost,
    removeEmailSequence,
    removeVideoScript,
  };
};
